import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

type TFunc = ReturnType<typeof useTranslation>['t'];

/**
 * Zusammenfassung eines Textfeldes fuer den Aufklapper-Kopf.
 *
 * Der leere Fall ist bewusst eigen: "unveraendert" waere formal richtig und
 * praktisch irrefuehrend, es klaenge, als staende dort etwas.
 */
export function summarizeText(before: string, after: string, t: TFunc): string {
  const b = before.trim();
  const a = after.trim();
  if (b === '' && a === '') return t('collapsible.empty');
  if (b === '') return t('collapsible.brandNew');
  if (b === a) return t('collapsible.unchanged');
  return t('collapsible.reworked');
}

/** Zusammenfassung einer Liste: wie viele, und wie viele davon neu. */
/**
 * Die Vergleichsform fuer Listeneintraege.
 *
 * Gross-/Kleinschreibung und Randabstaende sind keine Aenderung. Der Export
 * ist der Punkt: die Kopfzahl ("3, davon 2 neu") und die Marken an den
 * Zeilen im Editor MUESSEN dieselbe Regel benutzen. Sie taten es nicht - die
 * Kopfzahl verglich exakt, die Marken normalisiert. Ein umgeschriebenes
 * "Aus Bio-Baumwolle" -> "aus Bio-Baumwolle" zaehlte oben als neu und trug
 * unten keine Marke, und dann widerspricht die Zahl dem, was darunter steht.
 */
export function listenSchluessel(text: string): string {
  return text.trim().toLowerCase();
}

export function summarizeList(before: string[], after: string[], t: TFunc): string {
  // Der Highlights-Editor laesst leere Zeilen zu; sie sind keine Eintraege.
  const clean = (list: string[]) => list.map((v) => v.trim()).filter((v) => v !== '');
  const b = clean(before);
  const a = clean(after);
  if (a.length === 0) return t('collapsible.empty');
  const known = new Set(b.map(listenSchluessel));
  const added = a.filter((v) => !known.has(listenSchluessel(v))).length;
  if (added === 0) return t('collapsible.listCount', { count: a.length });
  // "5, davon 5 neu" laesst den Leser zwei Zahlen vergleichen, um zu
  // erkennen, dass es dieselbe ist. Sind alle neu, sagt man das.
  if (added === a.length) return t('collapsible.listAllNew', { count: a.length });
  return t('collapsible.listNew', { total: a.length, added });
}

interface CollapsibleFieldProps {
  label: string;
  /** Kurze Auskunft im Kopf, z.B. "komplett neu" oder "3, davon 2 neu". */
  summary: string;
  /** Kennzeichnet den Aufklapper in Test und aria. */
  fieldId: string;
  children: ReactNode;
  defaultOpen?: boolean;
  /** Testkennung am Zusammenfassungstext. Rein diagnostisch. */
  summaryTestId?: string;
}

/**
 * Ein eingeklappter Langtext-Bereich mit Zusammenfassung im Kopf.
 *
 * Die Zusammenfassung ist der Punkt. Ein reiner Aufklapper mit der
 * Beschriftung "Beschreibung" zwingt zum Aufklappen, um zu erfahren, ob sich
 * das Aufklappen lohnt. "komplett neu" beantwortet das vorher, und in einem
 * Durchlauf ueber tausende Produkte ist genau das der Unterschied.
 *
 * Der Aufklappzustand lebt lokal und wird beim Produktwechsel
 * zurueckgesetzt - die Karte gibt beiden Spalten dafuer einen `key`.
 */
export function CollapsibleField({
  label,
  summary,
  fieldId,
  children,
  defaultOpen = false,
  summaryTestId,
}: CollapsibleFieldProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-md border border-border">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          data-testid={`collapsible-${fieldId}`}
          className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition-colors hover:bg-muted/40"
        >
          <span className="flex min-w-0 items-center gap-2">
            <ChevronDown
              aria-hidden="true"
              className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
            />
            <span className="text-xs font-medium text-foreground">{label}</span>
          </span>
          <span data-testid={summaryTestId} className="shrink-0 text-xs text-muted-foreground">
            {summary}
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t border-border px-3 py-2">{children}</CollapsibleContent>
    </Collapsible>
  );
}
